<template>
  <!--
    1회 표시 값(관리자 비밀번호·복구 코드) 결과 모달. 값은 이 모달에서만 보이고, 닫히는 애니메이션이
    끝나면(after-close) 부모에 null 을 돌려 메모리에서도 버린다. 성공 토스트 대신 이 모달이 성공 표시다.
  -->
  <a-modal
    :open="visible"
    :footer="null"
    :mask-closable="false"
    destroy-on-close
    :after-close="onAfterClose"
    @cancel="visible = false"
  >
    <template #title>
      <div class="modal-title">{{ title }}</div>
    </template>

    <div v-if="reveal" class="modal-content pbx-dev-reveal">
      <template v-if="reveal.kind === 'adminPassword'">
        <div class="pbx-dev-reveal__value-row">
          <code class="pbx-dev-reveal__value">{{ reveal.value }}</code>
          <button type="button" class="pbx-dev-reveal__copy" @click="copyPbxText(reveal.value)">
            <CopyOutlined /> 복사
          </button>
        </div>
        <p class="pbx-dev-reveal__warn">
          이 창을 닫으면 다시 볼 수 없습니다(다시 열면 열람 기록이 또 남습니다). 현장 담당자 외에는 공유하지
          마세요.
        </p>
      </template>

      <template v-else>
        <div class="pbx-dev-reveal__value-row">
          <code class="pbx-dev-reveal__value pbx-dev-reveal__value--code">{{ reveal.code }}</code>
          <button type="button" class="pbx-dev-reveal__copy" @click="copyPbxText(reveal.code)">
            <CopyOutlined /> 복사
          </button>
        </div>
        <p class="pbx-dev-reveal__meta">만료 {{ expiresText }}</p>

        <div class="pbx-dev-reveal__fields">
          <p class="pbx-dev-reveal__fields-title">전화기에 입력할 3필드</p>
          <dl>
            <div>
              <dt>Boot Server</dt>
              <dd>Static</dd>
            </div>
            <div>
              <dt>Server Type</dt>
              <dd>HTTPS</dd>
            </div>
            <div>
              <dt>Server Address</dt>
              <dd>
                <code>{{ reveal.recovery_url }}</code>
                <button
                  type="button"
                  class="pbx-dev-reveal__copy pbx-dev-reveal__copy--mini"
                  title="Server Address 복사"
                  @click="copyPbxText(reveal.recovery_url)"
                >
                  <CopyOutlined />
                </button>
              </dd>
            </div>
          </dl>
        </div>
        <p class="pbx-dev-reveal__warn">
          24시간 안에 유효하고, 첫 요청한 전화기(MAC)에 묶입니다. 병원 직원에게 전화로 이 3줄만 불러 주세요.
        </p>
      </template>

      <div class="modal-footer pbx-dev-reveal__footer">
        <a-button type="primary" class="yellow-button" @click="visible = false">닫기</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { CopyOutlined } from '@ant-design/icons-vue';
import { computed, ref, watch } from 'vue';

import { copyPbxText } from '@/views/Callbot/service/usePbxAction';
import { formatPbxTime } from '@/views/Callbot/service/pbxExtensions';

/**
 * reveal — 1회 표시 값(PbxDeviceShared 의 PbxDeviceReveal 형태):
 *   { kind: 'adminPassword', value } | { kind: 'recoveryCode', code, expires_at, recovery_url } | null
 */
const props = defineProps({
  reveal: { type: Object, default: null },
});
const emit = defineEmits(['update:reveal']);

const visible = ref(false);

watch(
  () => props.reveal,
  (r) => {
    if (r) visible.value = true;
  },
  { immediate: true }
);

// antdv 4 의 afterClose 는 이벤트가 아니라 prop 이다(@after-close 로 걸면 호출되지 않아 값이 남는다)
function onAfterClose() {
  emit('update:reveal', null);
}

const title = computed(() => (props.reveal?.kind === 'recoveryCode' ? '복구 코드' : '관리자 비밀번호'));
const expiresText = computed(() =>
  props.reveal?.kind === 'recoveryCode' ? formatPbxTime(props.reveal.expires_at) : ''
);
</script>

<style scoped lang="scss">
.pbx-dev-reveal {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
  font-size: 13px;

  // admin 모달 규약 — 전역 .modal-content 의 테두리·최소 높이는 antd 모달 안에서 쓰지 않는다
  &.modal-content {
    border: none;
    border-radius: 0;
    min-height: 0;
  }

  &__value-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__value {
    flex: 1;
    min-width: 0;
    padding: 10px 14px;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    background: #fafafa;
    color: #262626;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0.04em;
    word-break: break-all;
    user-select: all;

    // 전화로 불러 주는 코드 — 크게
    &--code {
      font-size: 28px;
      letter-spacing: 0.2em;
      text-align: center;
    }
  }

  &__copy {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    background: #fff;
    color: #595959;
    font-size: 13px;
    cursor: pointer;

    &:hover {
      border-color: #1668dc;
      color: #1668dc;
    }

    &--mini {
      padding: 2px 6px;
      font-size: 12px;
    }
  }

  &__meta {
    margin: 0;
    color: #8c8c8c;
    font-size: 12px;
  }

  &__fields {
    padding: 12px 14px;
    border: 1px solid #f0f0f0;
    border-radius: 8px;

    dl {
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    div {
      display: flex;
      gap: 12px;
    }

    dt {
      flex: none;
      width: 110px;
      color: #8c8c8c;
    }

    dd {
      margin: 0;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 6px;
      color: #262626;
      font-weight: 600;

      code {
        word-break: break-all;
        user-select: all;
      }
    }
  }

  &__fields-title {
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 600;
    color: #595959;
  }

  &__warn {
    margin: 0;
    padding: 8px 12px;
    border-radius: 8px;
    background: #fffbe6;
    color: #d48806;
    font-size: 12px;
    line-height: 1.5;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}

/* 전역 정의가 없는 클래스라 쓰는 컴포넌트마다 들고 있어야 한다. */
:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
